/**
 * sl-9qm.5.1: YjsViewDataSource — Y.js-backed data source for Builder + Internal modes
 *
 * Reads data from BlockSuite database blocks via DatabaseBlockDataSource.
 * Applies filter/sort/pagination client-side.
 * Supports live updates via rows$/properties$ subscriptions.
 *
 * SOLID:
 * - S: Only data retrieval + client-side query processing
 * - L: Substitutable with MaterializedViewDataSource (sl-9qm.5.2)
 * - D: Depends on IViewDataSource interface, DocsService abstraction
 */
import {
  createDatabaseBlockDataSource,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { Service } from '@toeverything/infra';
import { Observable } from 'rxjs';

import { DocsService } from '../../doc';
import type {
  DataFilter,
  DataFilterOp,
  DataSort,
} from '../types/view-config.types';
import type {
  ColumnInfo,
  DatabaseViewData,
  DataSourceConfig,
  IViewDataSource,
  Row,
} from '../types/view-datasource.types';

/**
 * Apply a single filter condition to a cell value.
 */
function matchFilter(cellValue: unknown, filter: DataFilter): boolean {
  const { op, value: filterValue } = filter;

  switch (op as DataFilterOp) {
    case 'isEmpty':
      return cellValue === null || cellValue === undefined || cellValue === '';
    case 'isNotEmpty':
      return cellValue !== null && cellValue !== undefined && cellValue !== '';
    case 'eq':
      return String(cellValue) === String(filterValue);
    case 'neq':
      return String(cellValue) !== String(filterValue);
    case 'contains':
      return String(cellValue ?? '')
        .toLowerCase()
        .includes(String(filterValue ?? '').toLowerCase());
    case 'gt':
      return Number(cellValue) > Number(filterValue);
    case 'lt':
      return Number(cellValue) < Number(filterValue);
    case 'gte':
      return Number(cellValue) >= Number(filterValue);
    case 'lte':
      return Number(cellValue) <= Number(filterValue);
    default:
      return true;
  }
}

/**
 * Apply filters to rows. All filters are AND-combined.
 */
function applyFilters(rows: Row[], filters: DataFilter[]): Row[] {
  if (filters.length === 0) return rows;
  return rows.filter(row =>
    filters.every(f => matchFilter(row.cells[f.columnId], f))
  );
}

/**
 * Apply multi-column sort to rows.
 */
function applySorts(rows: Row[], sorts: DataSort[]): Row[] {
  if (sorts.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const sort of sorts) {
      const aVal = a.cells[sort.columnId];
      const bVal = b.cells[sort.columnId];
      const aStr = String(aVal ?? '');
      const bStr = String(bVal ?? '');
      const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
      if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
    }
    return 0;
  });
}

/**
 * Apply pagination to rows.
 */
function applyPagination(
  rows: Row[],
  pagination?: { page: number; pageSize: number }
): Row[] {
  if (!pagination) return rows;
  const { page, pageSize } = pagination;
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

export class YjsViewDataSource extends Service implements IViewDataSource {
  readonly writable = true;

  constructor(private readonly docsService: DocsService) {
    super();
  }

  getViewData(config: DataSourceConfig): Observable<DatabaseViewData> {
    return new Observable<DatabaseViewData>(subscriber => {
      if (!config.databaseDocId || !config.databaseBlockId) {
        subscriber.next({
          columns: [],
          rows: [],
          totalCount: 0,
          writable: this.writable,
        });
        subscriber.complete();
        return;
      }

      let released = false;
      const openResult = this.docsService.open(config.databaseDocId);
      const { doc, release } = openResult;
      const block = doc.blockSuiteDoc.getBlock(config.databaseBlockId);
      const model = (block?.model as DatabaseBlockModel | undefined) ?? null;
      let dataSource = this.createReadyDataSource(model);
      let storageDisposable: { unsubscribe: () => void } | undefined;

      const attachStorageSubscription = () => {
        storageDisposable?.unsubscribe();
        const storageDoc = (dataSource?.storageModel as any)?.store;
        storageDisposable = storageDoc?.slots?.blockUpdated?.subscribe?.(() => {
          emitData();
        });
      };

      const ensureDataSource = () => {
        if (dataSource || !model) return;
        dataSource = this.createReadyDataSource(model);
        if (dataSource) attachStorageSubscription();
      };

      const emitData = () => {
        ensureDataSource();
        try {
          const data = dataSource
            ? this.readDatabaseData(dataSource, config)
            : {
                columns: [],
                rows: [],
                totalCount: 0,
                writable: this.writable,
              };
          subscriber.next(data);
        } catch (err) {
          console.error('[YjsViewDataSource] Error reading data:', err);
          subscriber.next({
            columns: [],
            rows: [],
            totalCount: 0,
            writable: this.writable,
          });
        }
      };

      // Initial emission
      emitData();

      // Subscribe to block updates for live reactivity
      const blockSuiteDoc = doc.blockSuiteDoc;
      const hostDisposable = blockSuiteDoc.slots.blockUpdated.subscribe(() => {
        emitData();
      });
      if (dataSource) attachStorageSubscription();
      const readyDisposable = model
        ? observeDatabaseStorageModelReady(model, () => emitData())
        : undefined;

      return () => {
        hostDisposable.unsubscribe();
        storageDisposable?.unsubscribe?.();
        readyDisposable?.();
        if (!released) {
          released = true;
          release();
        }
      };
    });
  }

  getRow(config: DataSourceConfig, rowId: string): Observable<Row | null> {
    return new Observable<Row | null>(subscriber => {
      if (!config.databaseDocId || !config.databaseBlockId) {
        subscriber.next(null);
        subscriber.complete();
        return;
      }

      let released = false;
      const { doc, release } = this.docsService.open(config.databaseDocId);
      const block = doc.blockSuiteDoc.getBlock(config.databaseBlockId);
      const model = (block?.model as DatabaseBlockModel | undefined) ?? null;
      let dataSource = this.createReadyDataSource(model);
      const ensureDataSource = () => {
        if (dataSource || !model) return;
        dataSource = this.createReadyDataSource(model);
      };

      const emitRow = () => {
        ensureDataSource();
        try {
          const data = dataSource
            ? this.readDatabaseData(dataSource, config)
            : { columns: [], rows: [], totalCount: 0, writable: this.writable };
          const row = data.rows.find(r => r.id === rowId) ?? null;
          subscriber.next(row);
        } catch {
          subscriber.next(null);
        }
      };

      emitRow();

      const disposable = doc.blockSuiteDoc.slots.blockUpdated.subscribe(() =>
        emitRow()
      );
      const readyDisposable = model
        ? observeDatabaseStorageModelReady(model, () => emitRow())
        : undefined;

      return () => {
        disposable.unsubscribe();
        readyDisposable?.();
        if (!released) {
          released = true;
          release();
        }
      };
    });
  }

  updateCell(
    config: DataSourceConfig,
    rowId: string,
    columnId: string,
    value: unknown
  ): void {
    if (!config.databaseDocId || !config.databaseBlockId) return;

    const { doc, release } = this.docsService.open(config.databaseDocId);
    try {
      const block = doc.blockSuiteDoc.getBlock(config.databaseBlockId);
      const dataSource = this.createReadyDataSource(
        (block?.model as DatabaseBlockModel | undefined) ?? null
      );
      if (!dataSource) return;
      dataSource.cellValueChange(
        rowId,
        columnId === '_title' ? 'title' : columnId,
        value
      );
    } finally {
      release();
    }
  }

  /**
   * Read database data from a BlockSuite doc, applying client-side query.
   */

  private createReadyDataSource(
    model: DatabaseBlockModel | null
  ): ReturnType<typeof createDatabaseBlockDataSource> | null {
    if (!model || !isDatabaseStorageModelReady(model)) return null;
    return createDatabaseBlockDataSource(model);
  }

  private readDatabaseData(
    dataSource: ReturnType<typeof createDatabaseBlockDataSource>,
    config: DataSourceConfig
  ): DatabaseViewData {
    // Extract columns
    const storageModel = dataSource.storageModel as any;
    const rawColumns = (storageModel.props.columns ?? []) as Array<{
      id: string;
      name: string;
      type: string;
    }>;
    const columns: ColumnInfo[] = [
      // Title column is always first (built-in, not in props.columns)
      { id: '_title', name: 'Title', type: 'title', isPrimary: true },
      ...rawColumns.map(col => ({
        id: col.id,
        name: col.name,
        type: col.type,
      })),
    ];

    // Filter to visible columns if specified
    const visibleColumns = config.visibleColumns;
    const filteredColumns = visibleColumns?.length
      ? columns.filter(c => visibleColumns.includes(c.id))
      : columns;

    const totalRows = dataSource.rowCount$.value;
    const rowWindow = dataSource.getRowsWindow({
      offset: 0,
      limit: Math.max(totalRows, 1),
    });

    let rows: Row[] = rowWindow.rowIds.map(rowId => {
      const rowCells: Record<string, unknown> = {};
      rowCells['_title'] = dataSource.cellValueGet(rowId, 'title') ?? '';
      for (const column of rawColumns) {
        rowCells[column.id] = dataSource.cellValueGet(rowId, column.id);
      }

      return { id: rowId, cells: rowCells };
    });

    // Apply client-side query pipeline
    rows = applyFilters(rows, config.filters ?? []);
    const totalCount = rows.length;
    rows = applySorts(rows, config.sorts ?? []);
    rows = applyPagination(rows, config.pagination);

    return {
      columns: filteredColumns,
      rows,
      totalCount,
      writable: this.writable,
    };
  }
}
